/**
 * Onderdeel Toevoegen/Bewerken Formulier
 * Type selecteert een subgroep uit de database
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiFetch, { getApiBaseUrl } from '../../services/api';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import './Onderdelen.css';

interface OnderdeelDocument {
  id: number;
  voorraad_item_id: number;
  document_type: string;
  titel: string | null;
  bestand_pad: string;
  taal: string | null;
  versie: string | null;
}

interface GekoppeldOnderdeel {
  id: number;
  parent_item_id: number;
  child_item_id: number;
  onderdeel_type: string | null;
  vervangingsinterval_uren: number | null;
  vervangingsinterval_maanden: number | null;
  aantal: number;
  verplicht: boolean;
  opmerkingen: string | null;
  child_item?: {
    id: number;
    onderdeel_type: string | null;
    fabrikant: string | null;
    model: string | null;
    artikelnummer: string | null;
  };
}

interface VoorraadGroep {
  id: number;
  parent_id: number | null;
  categorie_id: number;
  naam: string;
  niveau: number;
  actief: boolean;
}

interface VoorraadCategorie {
  id: number;
  code: string;
  naam: string;
}

const OnderdeelForm = () => {
  const { onderdeelId } = useParams<{ onderdeelId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = !!onderdeelId;

  // Haal groep_id uit query parameters (voorgeselecteerd als je vanuit een groep komt)
  const groepIdParam = searchParams.get('groep_id');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data uit database
  const [leveranciers, setLeveranciers] = useState<string[]>([]);
  const [categorieen, setCategorieen] = useState<VoorraadCategorie[]>([]);
  const [alleGroepen, setAlleGroepen] = useState<VoorraadGroep[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Geselecteerde categorie en groep
  const [selectedCategorieId, setSelectedCategorieId] = useState<number | null>(null);
  const [selectedGroepId, setSelectedGroepId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    fabrikant: '',
    model: '',
    leverancier: '',
    artikelnummer: '',
    levensduur_maanden: '',
    inkoopprijs: '',
    verkoopprijs: '',
    aantal_voorraad: '',
    min_voorraad: '',
    // Motor-specifieke velden
    vermogen_pk: '',
    vermogen_kw: '',
    cilinders: '',
    brandstof_type: '',
    koelsysteem: ''
  });

  // Zoekbare leverancier dropdown
  const [leverancierSearch, setLeverancierSearch] = useState('');
  const [leverancierDropdownOpen, setLeverancierDropdownOpen] = useState(false);

  // Documenten (alleen bij bewerken)
  const [documenten, setDocumenten] = useState<OnderdeelDocument[]>([]);
  const [loadingDocumenten, setLoadingDocumenten] = useState(false);

  // Gekoppelde onderdelen (alleen bij bewerken)
  const [gekoppeldeOnderdelen, setGekoppeldeOnderdelen] = useState<GekoppeldOnderdeel[]>([]);
  const [loadingOnderdelen, setLoadingOnderdelen] = useState(false);
  const [selectedOnderdelen, setSelectedOnderdelen] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (isEditing) {
      loadOnderdeel();
      loadDocumenten();
      loadGekoppeldeOnderdelen();
    }
  }, [onderdeelId, alleGroepen]);

  // Als groep_id is meegegeven, selecteer de juiste categorie en groep
  useEffect(() => {
    if (groepIdParam && alleGroepen.length > 0 && categorieen.length > 0) {
      const groepId = parseInt(groepIdParam);
      const groep = alleGroepen.find(g => g.id === groepId);
      if (groep) {
        setSelectedCategorieId(groep.categorie_id);
        setSelectedGroepId(groepId);
      }
    }
  }, [groepIdParam, alleGroepen, categorieen]);

  // Click outside handler om dropdown te sluiten
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-select-leverancier')) {
        setLeverancierDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoadingData(true);
      // Laad leveranciers, categorieën en alle groepen parallel
      const [leveranciersData, categorieenData] = await Promise.all([
        apiFetch('/api/v1/voorraad/leveranciers'),
        apiFetch('/api/v1/voorraad/categorieen')
      ]);
      setLeveranciers(leveranciersData);
      setCategorieen(categorieenData);

      // Laad alle groepen van alle categorieën (inclusief subgroepen)
      const alleGroepenData: VoorraadGroep[] = [];
      for (const cat of categorieenData) {
        const groepenData = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${cat.id}&alle=true`);
        alleGroepenData.push(...groepenData);
      }
      setAlleGroepen(alleGroepenData);
    } catch (err) {
      console.error('Fout bij laden data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const loadOnderdeel = async () => {
    if (!onderdeelId || alleGroepen.length === 0) return;

    try {
      const data = await apiFetch(`/api/v1/voorraad/items/${onderdeelId}`);

      // Zoek de groep om de categorie te bepalen
      const groep = alleGroepen.find(g => g.id === data.groep_id);
      if (groep) {
        setSelectedCategorieId(groep.categorie_id);
        setSelectedGroepId(data.groep_id);
      }

      setFormData({
        fabrikant: data.fabrikant || '',
        model: data.model || '',
        leverancier: data.leverancier || '',
        artikelnummer: data.artikelnummer || '',
        levensduur_maanden: data.levensduur_maanden ? String(data.levensduur_maanden) : '',
        inkoopprijs: data.inkoopprijs ? String(data.inkoopprijs) : '',
        verkoopprijs: data.verkoopprijs ? String(data.verkoopprijs) : '',
        /* Niet op waarheid toetsen maar op null: 0 is een geldige waarde voor deze
           twee, en `0 ? ... : ''` maakt het veld leeg. Sla je zo'n leeg veld daarna
           op, dan wordt de 0 een null - en dat wist stilletjes wat je had ingesteld. */
        aantal_voorraad: data.aantal_voorraad != null ? String(data.aantal_voorraad) : '',
        min_voorraad: data.min_voorraad != null ? String(data.min_voorraad) : '',
        // Motor-specifieke velden
        vermogen_pk: data.vermogen_pk ? String(data.vermogen_pk) : '',
        vermogen_kw: data.vermogen_kw ? String(data.vermogen_kw) : '',
        cilinders: data.cilinders ? String(data.cilinders) : '',
        brandstof_type: data.brandstof_type || '',
        koelsysteem: data.koelsysteem || ''
      });

      if (data.leverancier) {
        setLeverancierSearch(data.leverancier);
      }
    } catch (err: any) {
      setError(err.message || 'Kon onderdeel niet laden');
    }
  };

  const loadDocumenten = async () => {
    if (!onderdeelId) return;
    try {
      setLoadingDocumenten(true);
      const data = await apiFetch(`/api/v1/onderdeel-documenten?voorraad_item_id=${onderdeelId}`);
      setDocumenten(data);
    } catch (err) {
      console.error('Fout bij laden documenten:', err);
    } finally {
      setLoadingDocumenten(false);
    }
  };

  const loadGekoppeldeOnderdelen = async () => {
    if (!onderdeelId) return;
    try {
      setLoadingOnderdelen(true);
      const data = await apiFetch(`/api/v1/voorraad-item-onderdelen/by-parent/${onderdeelId}`);
      setGekoppeldeOnderdelen(data);
    } catch (err) {
      console.error('Fout bij laden gekoppelde onderdelen:', err);
    } finally {
      setLoadingOnderdelen(false);
    }
  };

  const handleOpenDocument = async (doc: OnderdeelDocument) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${getApiBaseUrl()}/api/v1/onderdeel-documenten/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        setError(`Fout bij ophalen document: ${error.detail || 'Onbekende fout'}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err: any) {
      setError(`Fout bij ophalen document: ${err.message || 'Onbekende fout'}`);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (!window.confirm('Weet je zeker dat je dit document wilt verwijderen?')) return;
    try {
      await apiFetch(`/api/v1/onderdeel-documenten/${docId}`, { method: 'DELETE' });
      setDocumenten(documenten.filter(d => d.id !== docId));
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen document');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Groepen gefilterd op geselecteerde categorie
  const groepenVoorCategorie = alleGroepen.filter(g => g.categorie_id === selectedCategorieId);

  // Gefilterde leveranciers
  const filteredLeveranciers = leveranciers.filter(lev =>
    lev.toLowerCase().includes(leverancierSearch.toLowerCase())
  );

  // Leverancier selectie handler
  const handleLeverancierSelect = (lev: string) => {
    setFormData(prev => ({ ...prev, leverancier: lev }));
    setLeverancierSearch(lev);
    setLeverancierDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedGroepId) {
      setError('Selecteer een type (subgroep)');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const leverancierValue = formData.leverancier;

      // Bepaal onderdeel_type op basis van geselecteerde groep
      const groep = alleGroepen.find(g => g.id === selectedGroepId);
      const onderdeelType = groep ? groep.naam : '';

      const payload: any = {
        groep_id: selectedGroepId,
        onderdeel_type: onderdeelType,
        fabrikant: formData.fabrikant || null,
        model: formData.model || null,
        leverancier: leverancierValue || null,
        artikelnummer: formData.artikelnummer || null,
        levensduur_maanden: formData.levensduur_maanden ? parseInt(formData.levensduur_maanden) : null,
        inkoopprijs: formData.inkoopprijs ? parseFloat(formData.inkoopprijs) : null,
        verkoopprijs: formData.verkoopprijs ? parseFloat(formData.verkoopprijs) : null,
        aantal_voorraad: formData.aantal_voorraad !== '' ? parseInt(formData.aantal_voorraad) : 0,
        min_voorraad: formData.min_voorraad !== '' ? parseInt(formData.min_voorraad) : null,
        // Motor-specifieke velden
        vermogen_pk: formData.vermogen_pk ? parseFloat(formData.vermogen_pk) : null,
        vermogen_kw: formData.vermogen_kw ? parseFloat(formData.vermogen_kw) : null,
        cilinders: formData.cilinders ? parseInt(formData.cilinders) : null,
        brandstof_type: formData.brandstof_type || null,
        koelsysteem: formData.koelsysteem || null
      };

      if (isEditing) {
        await apiFetch(`/api/v1/voorraad/items/${onderdeelId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        setSuccess('Onderdeel bijgewerkt!');
      } else {
        await apiFetch('/api/v1/voorraad/items', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        setSuccess('Onderdeel toegevoegd!');
        // Reset form (behoud categorie en groep selectie)
        setFormData({
          fabrikant: '',
          model: '',
          leverancier: '',
          artikelnummer: '',
          levensduur_maanden: '',
          inkoopprijs: '',
          verkoopprijs: '',
          aantal_voorraad: '',
          min_voorraad: '',
          // Motor-specifieke velden
          vermogen_pk: '',
          vermogen_kw: '',
          cilinders: '',
          brandstof_type: '',
          koelsysteem: ''
        });
        setLeverancierSearch('');
      }
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isEditing ? 'Onderdeel Bewerken' : 'Nieuw Onderdeel Toevoegen'}</h1>
      </div>


      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit} className="onderdeel-form">
        <div className="form-section">
          <div className="form-grid">
            {/* Categorie dropdown */}
            <div className="form-group">
              <label htmlFor="categorie">Categorie *</label>
              <select
                id="categorie"
                value={selectedCategorieId || ''}
                onChange={(e) => {
                  const catId = e.target.value ? parseInt(e.target.value) : null;
                  setSelectedCategorieId(catId);
                  setSelectedGroepId(null); // Reset groep bij categorie wijziging
                }}
                disabled={loadingData}
                required
              >
                <option value="">-- Selecteer categorie --</option>
                {categorieen.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.naam}</option>
                ))}
              </select>
            </div>

            {/* Type/Groep dropdown - hiërarchisch */}
            <div className="form-group">
              <label htmlFor="groep">Type *</label>
              <select
                id="groep"
                value={selectedGroepId || ''}
                onChange={(e) => {
                  setSelectedGroepId(e.target.value ? parseInt(e.target.value) : null);
                }}
                disabled={loadingData || !selectedCategorieId}
                required
              >
                <option value="">-- Selecteer type --</option>
                {(() => {
                  // Bouw hiërarchische lijst
                  const renderGroepen = (parentId: number | null, niveau: number): JSX.Element[] => {
                    return groepenVoorCategorie
                      .filter(g => g.parent_id === parentId)
                      .map(groep => {
                        const prefix = '─ '.repeat(niveau);
                        const children = renderGroepen(groep.id, niveau + 1);
                        return [
                          <option key={groep.id} value={groep.id}>
                            {prefix}{groep.naam}
                          </option>,
                          ...children
                        ];
                      })
                      .flat();
                  };
                  return renderGroepen(null, 0);
                })()}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fabrikant">Fabrikant</label>
              <input
                type="text"
                id="fabrikant"
                name="fabrikant"
                value={formData.fabrikant}
                onChange={handleChange}
                placeholder="Bijv: Icom"
              />
            </div>

            <div className="form-group">
              <label htmlFor="model">Model</label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="Bijv: IC-M423G"
              />
            </div>

            {/* Leverancier - zoekbare dropdown */}
            <div className="form-group">
              <label htmlFor="leverancier">Leverancier</label>
              <div className="searchable-select searchable-select-leverancier">
                <input
                  type="text"
                  value={leverancierSearch}
                  onChange={(e) => {
                    setLeverancierSearch(e.target.value);
                    setLeverancierDropdownOpen(true);
                    if (e.target.value !== formData.leverancier) {
                      setFormData(prev => ({ ...prev, leverancier: '' }));
                    }
                  }}
                  onFocus={() => setLeverancierDropdownOpen(true)}
                  placeholder="Zoek of typ leverancier..."
                  disabled={loadingData}
                  autoComplete="off"
                />
                {leverancierDropdownOpen && !loadingData && (
                  <div className="dropdown-list" style={{ maxHeight: '200px' }}>
                    {filteredLeveranciers.length > 0 ? (
                      filteredLeveranciers.map(lev => (
                        <div
                          key={lev}
                          className={`dropdown-item ${formData.leverancier === lev ? 'selected' : ''}`}
                          onClick={() => handleLeverancierSelect(lev)}
                        >
                          {lev}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item" style={{ color: '#64748b' }}>
                        {leverancierSearch ? 'Geen leveranciers gevonden' : 'Geen leveranciers beschikbaar'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>


            <div className="form-group">
              <label htmlFor="artikelnummer">Artikelnr.</label>
              <input
                type="text"
                id="artikelnummer"
                name="artikelnummer"
                value={formData.artikelnummer}
                onChange={handleChange}
                placeholder="Bijv: NG-12345"
              />
            </div>

            <div className="form-group">
              <label htmlFor="levensduur_maanden">Levensduur (maanden)</label>
              <input
                type="number"
                id="levensduur_maanden"
                name="levensduur_maanden"
                value={formData.levensduur_maanden}
                onChange={handleChange}
                placeholder="Bijv: 60"
              />
            </div>

            <div className="form-group">
              <label htmlFor="inkoopprijs">Inkoopprijs (€)</label>
              <input
                type="number"
                id="inkoopprijs"
                name="inkoopprijs"
                value={formData.inkoopprijs}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="Bijv: 299.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="verkoopprijs">Verkoopprijs (€)</label>
              <input
                type="number"
                id="verkoopprijs"
                name="verkoopprijs"
                value={formData.verkoopprijs}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="Bijv: 399.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="aantal_voorraad">Aantal</label>
              <input
                type="number"
                id="aantal_voorraad"
                name="aantal_voorraad"
                value={formData.aantal_voorraad}
                onChange={handleChange}
                min="0"
                placeholder="Bijv: 10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="min_voorraad">Min. Voorraad</label>
              <input
                type="number"
                id="min_voorraad"
                name="min_voorraad"
                value={formData.min_voorraad}
                onChange={handleChange}
                min="0"
                placeholder="Bijv: 2"
              />
            </div>

            {/* Motor-specifieke velden - alleen tonen voor Motoren groep */}
            {(() => {
              const groep = alleGroepen.find(g => g.id === selectedGroepId);
              const isMotorGroep = groep?.naam === 'Motoren';
              
              if (!isMotorGroep) return null;
              
              return (
                <>
                  <div className="form-group">
                    <label htmlFor="vermogen_pk">Vermogen (pk)</label>
                    <input
                      type="number"
                      id="vermogen_pk"
                      name="vermogen_pk"
                      value={formData.vermogen_pk}
                      onChange={handleChange}
                      step="0.1"
                      min="0"
                      placeholder="Bijv: 40"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="vermogen_kw">Vermogen (kW)</label>
                    <input
                      type="number"
                      id="vermogen_kw"
                      name="vermogen_kw"
                      value={formData.vermogen_kw}
                      onChange={handleChange}
                      step="0.1"
                      min="0"
                      placeholder="Bijv: 29.8"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="cilinders">Cilinders</label>
                    <input
                      type="number"
                      id="cilinders"
                      name="cilinders"
                      value={formData.cilinders}
                      onChange={handleChange}
                      min="1"
                      placeholder="Bijv: 4"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="brandstof_type">Brandstof Type</label>
                    <select
                      id="brandstof_type"
                      name="brandstof_type"
                      value={formData.brandstof_type}
                      onChange={handleChange}
                    >
                      <option value="">-- Selecteer --</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Benzine">Benzine</option>
                      <option value="Elektrisch">Elektrisch</option>
                      <option value="Hybride">Hybride</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="koelsysteem">Koelsysteem</label>
                    <select
                      id="koelsysteem"
                      name="koelsysteem"
                      value={formData.koelsysteem}
                      onChange={handleChange}
                    >
                      <option value="">-- Selecteer --</option>
                      <option value="Zoetwater">Zoetwater</option>
                      <option value="Zoutwater">Zoutwater</option>
                      <option value="Luchtgekoeld">Luchtgekoeld</option>
                      <option value="Gemengd">Gemengd</option>
                    </select>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="form-actions" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn-lg btn-primary"
              onClick={() => navigate('/relations/new?type=leverancier')}
            >
              + Nieuwe Relatie
            </button>
            <button
              type="button"
              className="btn-lg btn-secondary"
              onClick={() => navigate('/voorraad/nieuwe-subgroep')}
            >
              + Nieuwe Subgroep
            </button>
            {isEditing && (
              <button
                type="button"
                className="btn-lg btn-info"
                onClick={() => navigate(`/bestelpool/new?onderdeel_id=${onderdeelId}`)}
                style={{ backgroundColor: '#17a2b8', color: 'white' }}
              >
                Bestellen
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn-lg btn-secondary"
              onClick={() => navigate(-1)}
            >
              Annuleren
            </button>
            <button
              type="submit"
              className="btn-lg btn-success"
              disabled={saving}
            >
              {saving ? 'Opslaan...' : (isEditing ? 'Bijwerken' : 'Toevoegen')}
            </button>
          </div>
        </div>
      </form>

      {/* Gekoppelde onderdelen sectie - alleen bij bewerken */}
      {isEditing && (
        <div className="form-section" style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Onderdelen bij dit product</h2>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/voorraad/item/${onderdeelId}/onderdelen`)}
            >
              + Onderdeel Koppelen
            </button>
          </div>

          {loadingOnderdelen ? (
            <p>Onderdelen laden...</p>
          ) : gekoppeldeOnderdelen.length === 0 ? (
            <p style={{ color: '#64748b' }}>Nog geen onderdelen gekoppeld aan dit product.</p>
          ) : (
            <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedOnderdelen.length === gekoppeldeOnderdelen.length && gekoppeldeOnderdelen.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOnderdelen(gekoppeldeOnderdelen.map(k => k.child_item_id));
                          } else {
                            setSelectedOnderdelen([]);
                          }
                        }}
                      />
                    </th>
                    <th>Type</th>
                    <th>Fabrikant</th>
                    <th>Model</th>
                    <th>Artikelnr.</th>
                    <th>Aantal</th>
                    <th>Interval</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {gekoppeldeOnderdelen.map(koppeling => (
                    <tr key={koppeling.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOnderdelen.includes(koppeling.child_item_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOnderdelen([...selectedOnderdelen, koppeling.child_item_id]);
                            } else {
                              setSelectedOnderdelen(selectedOnderdelen.filter(id => id !== koppeling.child_item_id));
                            }
                          }}
                        />
                      </td>
                      <td>{koppeling.onderdeel_type || koppeling.child_item?.onderdeel_type || '-'}</td>
                      <td>{koppeling.child_item?.fabrikant || '-'}</td>
                      <td>{koppeling.child_item?.model || '-'}</td>
                      <td>{koppeling.child_item?.artikelnummer || '-'}</td>
                      <td>{koppeling.aantal}x</td>
                      <td>
                        {koppeling.vervangingsinterval_maanden
                          ? `${koppeling.vervangingsinterval_maanden} mnd`
                          : koppeling.vervangingsinterval_uren
                            ? `${koppeling.vervangingsinterval_uren} uur`
                            : '-'}
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn-small btn-success"
                          onClick={() => navigate(`/onderdelen/${koppeling.child_item_id}`)}
                        >
                          Bekijk
                        </button>
                        <button
                          type="button"
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/voorraad/item/${onderdeelId}/onderdelen`)}
                        >
                          Beheer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedOnderdelen.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-lg btn-info"
                  onClick={() => navigate(`/bestelpool/new?onderdeel_ids=${selectedOnderdelen.join(',')}`)}
                  style={{ backgroundColor: '#17a2b8', color: 'white' }}
                >
                  Bestel aangevinkt ({selectedOnderdelen.length})
                </button>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Documenten sectie - alleen bij bewerken */}
      {isEditing && (
        <div className="form-section" style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Documenten</h2>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/onderdelen/${onderdeelId}/documenten/new`)}
            >
              + Document Toevoegen
            </button>
          </div>

          {loadingDocumenten ? (
            <p>Documenten laden...</p>
          ) : documenten.length === 0 ? (
            <p style={{ color: '#64748b' }}>Nog geen documenten toegevoegd.</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Titel</th>
                    <th>Taal</th>
                    <th>Versie</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {documenten.map(doc => (
                    <tr key={doc.id}>
                      <td>{doc.document_type}</td>
                      <td>{doc.titel || '-'}</td>
                      <td>{doc.taal || '-'}</td>
                      <td>{doc.versie || '-'}</td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn-small btn-success"
                          onClick={() => handleOpenDocument(doc)}
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/onderdelen/${onderdeelId}/documenten/${doc.id}`)}
                        >
                          Bewerken
                        </button>
                        <button
                          type="button"
                          className="btn-small btn-danger"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          Verwijder
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default OnderdeelForm;
